import React, { useState } from 'react';
import styled, { css, keyframes } from 'styled-components';

import doorState from 'config/doorState';
import unloadSpeed from 'config/unloadSpeed';

import { Row, Col } from 'components/layout';
import { Text, Link } from 'components/Text';
import Icon from 'components/Icon';
import Statistics from 'components/Statistics';

const Board = ({ title, data, alerts, ...rest }) => {
  const [splitSize] = useState(12);
  const colSpan = 24 / splitSize;

  const splits = data.doors.reduce((splits, door, i) => {
    const splitNr = Math.floor(i / splitSize);
    const split = splits[splitNr] || [];
    splits[splitNr] = [...split, door];
    return splits;
  }, []);
  
  return (
    <StyledBoard {...rest}>
      <Row justify="space-between" align="middle" style={{ paddingLeft: '1.64vw', paddingBottom: '1.64vh', paddingRight: '0.5vw' }}>
        <Col>
          <Title>{title}</Title>
        </Col>
        <Col>
          <Row justify="end" align="center">
            {
              data.stats.map((stat, i) => (
                <StyledStatistics
                  {...stat}
                  value={stat.value !== undefined ? stat.value : '-'}
                  key={i}
                  titleAlign={['center', 'right']}
                />
              ))
            }
          </Row>
        </Col>
      </Row>
      
      <Row justify="start" align="stretch" style={{ marginLeft: '12px', flexWrap: 'nowrap' }}>
        <Col>
          {
            splits.map((split, i) => {
              return <Row key={i} justify="end" align="middle" style={{ height: '4.9vh', marginRight: '0.3vw' }}> 
                <Text key={i} style={{ color: '#000', fontSize: '0.84vw', fontWeight: 'bold', whiteSpace: 'pre-wrap', textAlign: 'right' }}>
                  {`${split[0].nr}\n${split[split.length - 1].nr}`}
                </Text>
              </Row>
            })
          }
        </Col>
        <Col flex={1}>
          {
            splits.map((split, i) => (
              <Row key={i}>
                {
                  split.map((door, i) => {
                    const hasAlerts = alerts.includes(door.door_id);

                    return (
                      <Col key={i} span={colSpan}>
                        <Link target="_blank" to={`/doors/${door.door_id}`}>
                          <Door door={door} hasAlerts={hasAlerts} />
                        </Link>
                      </Col>
                    );
                  })
                }
              </Row>
            ))
          }
        </Col>
      </Row>
    </StyledBoard>
  );
};

const Door = React.memo(({ door, hasAlerts }) => {
  if (!door) return null;

  const color = doorState.getColor(door.door_state) || unloadSpeed.getColor(door.relative_speed);
  const isNotStarted = door.door_state === doorState.NOT_STARTED;
  const noCameraColor = '#EFF2F5';
  const maintenanceMode = door.maintenance_mode === 1;
  const hasCamera = door.has_cam === 1 && !maintenanceMode;
  const isReverseDirection = hasCamera && door.default_direction !== door.direction && door.door_state !== doorState.EMPTY;
  const isFavorite = door.is_favorite === 1;
  const hasAnomaly = door.is_low_accuracy === 1;
  const isStorageEquipment = door.container_code === "Equipment used for Pallets" || door.container_code === "Equipment used for Storage";
  const pulse = hasCamera && hasAlerts;
  const unidentifiedEmployees = door.has_missing_tags || 0;
  const bgColor = (isStorageEquipment || !hasCamera) ? noCameraColor : color;
  const textColor = (isStorageEquipment || !hasCamera) ? '#232526' : '#ffffff';
  const hasTriangle = maintenanceMode || hasAnomaly;
  const showCameraOff = !hasCamera && !hasTriangle;
  const showArchive = isStorageEquipment && !hasTriangle && !showCameraOff;
  
  return (
    <DoorWrapper
      isReverseDirection={isReverseDirection}
      isNotStarted={isNotStarted}
      reverseColor={'#0A223B'}
      pulse={pulse}
      bgColor={bgColor}
    >
      <DoorName
        alternate={unidentifiedEmployees}
        style={{
          color: textColor
        }}
      >
        {door.name}
      </DoorName>
      <UnidentifiedPerson
        alternate={unidentifiedEmployees}
      >
        <Icon
          name="user"
          size={'2.4vh'}
          color={textColor}
          strokeWidth={2}
        />
      </UnidentifiedPerson>
      {isFavorite && <Icon
        name="star"
        size={'1vw'}
        fill="#FFF566"
        color="#FFF566"
        style={{ position: 'absolute', left: '1%', top: '1%' }}
      />}
      {showCameraOff && (
        <Icon
          name={'video-off'}
          size={'0.9vw'}
          color="#000000"
          strokeWidth={3}
          style={{ position: 'absolute', right: '7%', top: '1%' }}
        />
      )}
      {hasTriangle && (
        <Icon
          name={'alert-triangle'}
          size="0.8vw"
          color={maintenanceMode ? '#000000' : (hasCamera ? '#ffffff' : '#232526')}
          strokeWidth={3}
          style={{ position: 'absolute', right: '8%', bottom: '6%' }}
        />
      )}
      {showArchive && (
        <Icon
          name="archive"
          size={'0.8vw'}
          color={hasCamera ? '#000000' : '#232526'}
          strokeWidth={3}
          style={{ position: 'absolute', left: '8%', bottom: '6%' }}
        />
      )}
    </DoorWrapper>
  );

}, (prevProps, nextProps) => {
  if (prevProps.hasAlerts !== nextProps.hasAlerts) {
    return false;
  }

  return [
    'door_state',
    'has_cam',
    'is_favorite',
    'is_low_accuracy',
    'container_code',
    'has_missing_tags',
    'direction',
    'relative_speed',
  ].every(field => {
    if (!prevProps.door || !nextProps.door) {
      return false;
    }
    return prevProps.door[field] === nextProps.door[field];
  });
});

const StyledBoard = styled.div`
  display: block;
`;

const StyledStatistics = styled(Statistics)`
  line-height: 5vh;

  &:not(:last-child) {
    margin-right: 2.3vw;
  }

  & .ant-statistic-title {
    font-style: normal;
    font-weight: bold;
    font-size: 1.24vw;
    color: #45494D;
    text-align: start;
    margin-bottom: unset;

    color: ${props => props.color};
  }

  & .ant-statistic-content {
    margin-right: 0.4vw;
    font-style: normal;
    font-weight: bold;
    font-size: 1.74vw;
    color: ${props => props.color};
  }
`;

const DoorWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  font-weight: 600;
  font-size: 2.4vh;
  line-height: 2.7vh;
  margin-right: 0.5vh;
  margin-bottom: 0.5vh;

  cursor: pointer;
  height: 4.5vh;
  border-radius: 5%;
  border: 0.46vh solid;
  background-color: ${props => props.bgColor};

  ${props => props.isReverseDirection ? `
    border-color: #0A223B;
  ` : `
    border-color: transparent;
  `}

  ${props => props.isReverseDirection && props.isNotStarted ? `
    border-image-source: linear-gradient(78.72deg, #424242 0.19%, #8E8E8E 22.98%, #424242 49.9%, #979797 73.72%, #292929 99.61%);;
    border-image-slice: 10%;
  ` : null}

  ${props => props.pulse ? css`
    animation: ${pulseKeyframe} 0.6s ease infinite;
  ` : null}
`;

const DoorName = styled(Text)`
  ${props => props.alternate ? css`
    animation: ${fadeOutKeyframe};
    animation-duration: 6.25s;
    animation-fill-mode: both;
    animation-iteration-count: infinite;
    animation-direction: alternate-reverse;
    animation-timing-function: linear;
  ` : null}
`;
  
const UnidentifiedPerson = styled.div`
  position: absolute;
  inset: 0px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  opacity: 0;

  ${props => props.alternate ? css`
    animation: ${fadeInKeyframe};
    animation-duration: 6.25s;
    animation-fill-mode: both;
    animation-iteration-count: infinite;
    animation-direction: alternate;
    animation-timing-function: linear;
  ` : null}
`;

const Title = styled(Text)`
  font-weight: 600;
  font-size: 1.64vw;
  line-height: 5vh;
  color: #000;
`;

const pulseKeyframe = keyframes`
  0%, 100% {
    transform: scale(1.0)
  }
  50% {
    transform: scale(1.1)
  }
`;

const fadeInKeyframe = keyframes`
  0%, 40% {
    opacity: 0;
    // transform: translateY(-50%);
  }

  60%, 100% {
    opacity: 1;
    // transform: translateY(0%);
  }
`;

const fadeOutKeyframe = keyframes`
  0%, 40% {
    opacity: 0;
    // transform: translateY(50%);
  }

  60%, 100% {
    opacity: 1;
    // transform: translateY(0%);
  }
`;


export default Board;